import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { DashboardShell } from "@/components/DashboardShell";
import { KnowledgeHealth } from "@/components/KnowledgeHealth";
import {
  BrainCircuit,
  Globe,
  FileText,
  Trash2,
  Loader2,
  Plus,
  Search,
  UploadCloud,
} from "lucide-react";
import {
  addTextKnowledge,
  ingestWebsite,
  indexUploadedKnowledge,
  indexStoredDocument,
  listKnowledgeDocs,
  deleteKnowledgeDoc,
} from "@/lib/knowledge.functions";
import { supabase } from "@/integrations/supabase/client";
import { useHydratedTranslation } from "@/components/marketing/Layout";
const loadDocExtract = () => import("@/lib/document-extract");

import { buildSeo } from "@/lib/seo";

export const Route = createFileRoute("/_authenticated/knowledge")({
  head: () =>
    buildSeo({
      path: "/knowledge",
      title: "Knowledge Center — Reality Twin",
      description:
        "Feed your AI Twin with knowledge — upload documents, ingest websites and manage the sources that ground every Twin response.",
      noindex: true,
    }),
  component: KnowledgePage,
});

function KnowledgePage() {
  const t = useHydratedTranslation();
  const qc = useQueryClient();
  const list = useServerFn(listKnowledgeDocs);
  const addText = useServerFn(addTextKnowledge);
  const indexUpload = useServerFn(indexUploadedKnowledge);
  const indexStored = useServerFn(indexStoredDocument);
  const ingest = useServerFn(ingestWebsite);
  const del = useServerFn(deleteKnowledgeDoc);

  const { data, isLoading } = useQuery({
    queryKey: ["knowledge-docs"],
    queryFn: () => list(),
  });

  const [tab, setTab] = useState<"website" | "file" | "text">("website");
  const [url, setUrl] = useState("");
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const [filter, setFilter] = useState("");

  const ingestM = useMutation({
    mutationFn: async () => ingest({ data: { url } }),
    onSuccess: (r) => {
      const pages = (r as any).pages ?? 1;
      const remaining = (r as any).remaining ?? 0;
      toast.success(t("app.pages.knowledge.crawled", { pages, chunks: r.chunks }));
      if (remaining > 0) {
        toast.info(t("app.pages.knowledge.moreDiscovered", { n: remaining }));
      }
      setUrl("");
      qc.invalidateQueries({ queryKey: ["knowledge-docs"] });
    },
    onError: (e) => toast.error((e as Error).message),
  });

  const addM = useMutation({
    mutationFn: async () => addText({ data: { title, content } }),
    onSuccess: (r) => {
      toast.success(t("app.pages.knowledge.indexedChunks", { n: r.chunks }));
      setTitle("");
      setContent("");
      qc.invalidateQueries({ queryKey: ["knowledge-docs"] });
    },
    onError: (e) => toast.error((e as Error).message),
  });

  const delM = useMutation({
    mutationFn: async (id: string) => del({ data: { id } }),
    onSuccess: () => {
      toast.success(t("app.pages.knowledge.removed"));
      qc.invalidateQueries({ queryKey: ["knowledge-docs"] });
    },
    onError: (e) => toast.error((e as Error).message),
  });

  const docs = (data?.docs ?? []).filter(
    (d: any) => !filter || d.title.toLowerCase().includes(filter.toLowerCase()),
  );
  const stats = [
    { k: t("app.pages.knowledge.stSources"), v: String(data?.docs?.length ?? 0) },
    { k: t("app.pages.knowledge.stChunks"), v: String(data?.totalChunks ?? 0) },
    {
      k: t("app.pages.knowledge.stWebsites"),
      v: String((data?.docs ?? []).filter((d: any) => d.source_type === "website").length),
    },
    {
      k: t("app.pages.knowledge.stDocuments"),
      v: String((data?.docs ?? []).filter((d: any) => d.source_type === "upload").length),
    },
  ];

  async function uploadAndIndex() {
    if (files.length === 0) return;
    setUploading(true);
    try {
      const { data: userData, error: userError } = await supabase.auth.getUser();
      if (userError || !userData.user)
        throw new Error("Please sign in again before uploading documents.");
      let indexed = 0;
      const failed: { name: string; reason: string }[] = [];
      const { extractDocumentText, safeStorageFileName } = await loadDocExtract();

      // Per-file: upload to storage first so the server can always re-read
      // the original, then try fast in-browser extraction, falling back to
      // server-side extraction (unpdf/mammoth) if the browser cannot read it.
      for (const file of files) {
        try {
          const safeName = safeStorageFileName(file.name);
          const storagePath = `${userData.user.id}/${crypto.randomUUID()}-${safeName}`;
          const { error: uploadError } = await supabase.storage
            .from("knowledge")
            .upload(storagePath, file, {
              contentType: file.type || "application/octet-stream",
              upsert: false,
            });
          if (uploadError) throw uploadError;

          let result: { chunks: number };
          try {
            const extracted = await extractDocumentText(file);
            if (extracted.length < 20) throw new Error("browser extraction too short");
            result = await indexUpload({
              data: { title: file.name, content: extracted, storagePath, sizeBytes: file.size },
            });
          } catch {
            // Browser could not read the file — extract on the server instead.
            result = await indexStored({
              data: { title: file.name, storagePath, sizeBytes: file.size },
            });
          }
          indexed += result.chunks;
        } catch (e) {
          failed.push({ name: file.name, reason: (e as Error).message });
        }
      }

      const okCount = files.length - failed.length;
      if (okCount > 0) {
        toast.success(t("app.pages.knowledge.indexedDocs", { n: okCount }));
      }
      for (const f of failed) toast.error(`${f.name}: ${f.reason}`);
      setFiles(files.filter((f) => failed.some((x) => x.name === f.name)));
      qc.invalidateQueries({ queryKey: ["knowledge-docs"] });
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setUploading(false);
    }
  }

  return (
    <DashboardShell
      title={t("app.pages.knowledge.title")}
      subtitle={t("app.pages.knowledge.subtitle")}
    >
      <div className="grid lg:grid-cols-4 gap-4">
        {stats.map((s) => (
          <div key={s.k} className="glass-strong rounded-2xl p-5">
            <div className="text-xs uppercase tracking-widest text-muted-foreground font-mono">
              {s.k}
            </div>
            <div className="font-display text-3xl font-semibold mt-2">{s.v}</div>
          </div>
        ))}
      </div>

      <KnowledgeHealth />

      <div className="mt-4 grid lg:grid-cols-5 gap-4">
        <div className="lg:col-span-2 glass-strong rounded-2xl p-6">
          <div className="flex items-center gap-2 mb-4">
            <BrainCircuit className="h-5 w-5 text-[oklch(0.82_0.18_168)]" />
            <div className="font-semibold">{t("app.pages.knowledge.add")}</div>
          </div>
          <div className="flex gap-2 mb-4 text-xs flex-wrap">
            <button
              onClick={() => setTab("website")}
              className={`px-3 py-1.5 rounded-full border ${tab === "website" ? "brand-gradient text-black border-transparent" : "glass border-border"}`}
            >
              <Globe className="h-3 w-3 inline mr-1" /> {t("app.pages.knowledge.tWebsite")}
            </button>
            <button
              onClick={() => setTab("file")}
              className={`px-3 py-1.5 rounded-full border ${tab === "file" ? "brand-gradient text-black border-transparent" : "glass border-border"}`}
            >
              <UploadCloud className="h-3 w-3 inline mr-1" /> {t("app.pages.knowledge.tDocuments")}
            </button>
            <button
              onClick={() => setTab("text")}
              className={`px-3 py-1.5 rounded-full border ${tab === "text" ? "brand-gradient text-black border-transparent" : "glass border-border"}`}
            >
              <FileText className="h-3 w-3 inline mr-1" /> {t("app.pages.knowledge.tText")}
            </button>
          </div>

          {tab === "website" ? (
            <div className="space-y-3">
              <input
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder={t("app.pages.knowledge.urlPh")}
                className="w-full rounded-xl bg-white/5 border border-border px-4 py-2.5 text-sm focus:outline-none focus:border-[oklch(0.82_0.18_168_/_0.6)]"
              />
              <button
                onClick={() => ingestM.mutate()}
                disabled={ingestM.isPending || !url.trim()}
                className="w-full rounded-xl brand-gradient text-black py-2.5 text-sm font-medium disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {ingestM.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Plus className="h-4 w-4" />
                )}
                {t("app.pages.knowledge.crawlIndex")}
              </button>
              <p className="text-xs text-muted-foreground">
                {t("app.pages.knowledge.websiteHelp")}
              </p>
            </div>
          ) : tab === "file" ? (
            <div className="space-y-3">
              <label className="block rounded-2xl border border-dashed border-border bg-background/50 p-5 text-center cursor-pointer hover:border-foreground/30 transition">
                <UploadCloud className="mx-auto h-8 w-8 text-muted-foreground" />
                <div className="mt-3 text-sm font-medium">
                  {t("app.pages.knowledge.uploadDocs")}
                </div>
                <div className="mt-1 text-xs text-muted-foreground">
                  {t("app.pages.knowledge.uploadHint")}
                </div>
                <input
                  type="file"
                  multiple
                  accept=".pdf,.docx,.txt,.md,.markdown,.csv,.json,.html,.htm,.xml,.yaml,.yml,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/*"
                  onChange={(e) => setFiles(Array.from(e.target.files ?? []))}
                  className="sr-only"
                />
              </label>
              {files.length > 0 && (
                <div className="rounded-xl border border-border bg-background/50 divide-y divide-border">
                  {files.map((file) => (
                    <div
                      key={`${file.name}-${file.size}`}
                      className="px-3 py-2 text-xs flex items-center justify-between gap-3"
                    >
                      <span className="truncate">{file.name}</span>
                      <span className="text-muted-foreground shrink-0">
                        {Math.max(1, Math.round(file.size / 1024))} KB
                      </span>
                    </div>
                  ))}
                </div>
              )}
              <button
                onClick={uploadAndIndex}
                disabled={uploading || files.length === 0}
                className="w-full rounded-xl brand-gradient text-black py-2.5 text-sm font-medium disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {uploading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Plus className="h-4 w-4" />
                )}
                {uploading
                  ? t("app.pages.knowledge.extracting")
                  : t("app.pages.knowledge.uploadIndex")}
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              <input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder={t("app.pages.knowledge.titlePh")}
                className="w-full rounded-xl bg-white/5 border border-border px-4 py-2.5 text-sm focus:outline-none focus:border-[oklch(0.82_0.18_168_/_0.6)]"
              />
              <textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                rows={8}
                placeholder={t("app.pages.knowledge.contentPh")}
                className="w-full rounded-xl bg-white/5 border border-border px-4 py-2.5 text-sm focus:outline-none focus:border-[oklch(0.82_0.18_168_/_0.6)] resize-none"
              />
              <button
                onClick={() => addM.mutate()}
                disabled={addM.isPending || !title.trim() || content.trim().length < 20}
                className="w-full rounded-xl brand-gradient text-black py-2.5 text-sm font-medium disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {addM.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Plus className="h-4 w-4" />
                )}
                {t("app.pages.knowledge.addToKb")}
              </button>
            </div>
          )}
        </div>

        <div className="lg:col-span-3 glass-strong rounded-2xl p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="font-semibold">{t("app.pages.knowledge.sources")}</div>
            <div className="relative w-56">
              <Search className="h-3.5 w-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                placeholder={t("app.pages.knowledge.searchPh")}
                className="w-full pl-8 pr-3 py-1.5 text-xs rounded-full bg-white/5 border border-border focus:outline-none focus:border-[oklch(0.82_0.18_168_/_0.6)]"
              />
            </div>
          </div>
          {isLoading ? (
            <div className="text-sm text-muted-foreground py-8 text-center">
              {t("app.pages.knowledge.loading")}
            </div>
          ) : docs.length === 0 ? (
            <div className="text-sm text-muted-foreground py-8 text-center">
              {t("app.pages.knowledge.noSources")}
            </div>
          ) : (
            <div className="divide-y divide-border">
              {docs.map((d: any) => (
                <div key={d.id} className="py-3 flex items-center gap-3">
                  {d.source_type === "website" ? (
                    <Globe className="h-4 w-4 text-[oklch(0.82_0.18_168)] shrink-0" />
                  ) : (
                    <FileText className="h-4 w-4 text-[oklch(0.82_0.18_168)] shrink-0" />
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate">{d.title}</div>
                    <div className="text-xs text-muted-foreground truncate">
                      {d.source_type} · {d.status}
                      {d.error ? ` · ${d.error}` : ""}
                    </div>
                  </div>
                  <button
                    onClick={() => delM.mutate(d.id)}
                    disabled={delM.isPending}
                    className="opacity-50 hover:opacity-100 hover:text-destructive p-1"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </DashboardShell>
  );
}
